import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@app/services/auth/auth.service';
import { DynamicDropdownService } from '@app/services/dynamic-dropdown.service';
import { Messages } from '@app/src/util/messages';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-issue-tmpl-table',
  templateUrl: './issue-tmpl-table.component.html',
  styleUrls: ['./issue-tmpl-table.component.scss']
})
export class IssueTmplTableComponent implements OnInit {

  issueTmplForm: FormGroup; // Form group for issue template table
  displayedColumns: string[] = ['key', 'description', 'payload', 'default']; // Columns to be displayed in the table
  getNoData = Messages.no_data; //for getting no data available message
  issueTmpl: any[] = []; // Array to hold issue template data
  readOnlyFields: string[] = ["BUILDER_GMID"];

  // Input properties
  @Input() set input(data: any) {
    if (data) {
      this.issueTmpl = data; // Assign input data to issueTmpl
      this.issueTmpl.length > 0 ? this.addTableData(this.issueTmpl) : '';
    }

  }; // Input variable to receive data from parent component
  @Input() mode: any; // Mode of the component, can be 'view', 'add', or 'edit'

  /**Input variable when manual workflow is triggered */
  @Input() manualTrigger: boolean; // boolean value to check if manual workflow is triggered

  // Output properties
  @Output() issueTmplOutput = new EventEmitter<any>(); // Emit the issue template data to parent component

  /**
   * constructor
   * @param fb FormBuilder instance to create form controls
   * @param dpService DynamicDropdownService instance to handle dynamic dropdowns
   */
  constructor(private fb: FormBuilder,
    public dpService: DynamicDropdownService,
    private authService: AuthService) {
    // Initialize the form group and add table data
    this.initializeForm();
  }

  /** ngOnInit */
  ngOnInit() {
    this.columnChanges(); // Handle column changes based on manual trigger
    this.issueTmpl.length > 0 ? this.addTableData(this.issueTmpl) : ''; // Add table data if issueTmpl has values
    this.commonChanges(); // Handle initial form and subsequent changes to form
  }

  /** ngOnChanges */
  ngOnChanges(changes: SimpleChanges): void {
    this.disableForm();
    if (changes['input']) {
      this.commonChanges(); // Handle initial form and subsequent changes to form
    }
  }

  /** ngAfterViewInit */
  ngAfterViewInit() {
    this.disableForm();
  }

  /** Method to handle initial form and subsequent changes to form */
  commonChanges() {
    this.emitTableData(); // Emit initial table data
    this.valueChanges(); // Subscribe to value changes of issueTmplArray FormArray to emit data on changes
  }

  /** Method to subscribe to value changes of issueTmplArray FormArray */
  valueChanges() {
    this.issueTmplForm.get('issueTmplArray')?.valueChanges.pipe(debounceTime(200)).subscribe((val: any) => {
      this.emitTableData();
    });
  }

  // Getter for issueTmplArray FormArray
  get issueTmplArray(): FormArray {
    return this.issueTmplForm.get('issueTmplArray') as FormArray;
  }

  // Method to handle changes in the columns based on manual trigger
  columnChanges() {
    if (this.manualTrigger) {
      this.displayedColumns = ['key', 'description', 'default'];
    }
  }

  // Method to emit table data to parent component
  emitTableData() {
    this.issueTmplOutput.emit({ issueTmplData: [...this.issueTmplArray.value], isIssueTmplValid: this.issueTmplForm.valid });
  }

  /** disable enable form based on mode */
  disableForm() {
    if (this.mode === 'read') {
      this.issueTmplForm.disable(); // Disable the form if mode is 'read'
    }
    else if (this.mode === 'edit') {
      this.issueTmplForm.enable() // Enable the form if mode is 'edit'
    }
  }

  /* Method to create a FormGroup for each input */
  createFormGroup(input: any): FormGroup {
    // Set default value based on input type
    const defaultValue =
      input?.type === 'dropdown'
        ? input?.attributes?.options?.[input?.attributes?.default]
        : input?.attributes?.value ?? '';

    // Create and return a FormGroup with the necessary controls
    return this.fb.group({
      key: [input.id],
      type: [input.type],
      description: [input.attributes.description || ''],
      value: [defaultValue, input.validations?.required ? Validators.required : ''],
      defaultValue: [defaultValue],
      label: [input.attributes.label || ''],
      placeholder: [input.attributes.placeholder || ''],
      options: [input.attributes.options || ''],
      multiple: [input.attributes.multiple || false],
      isSelected: [false],
      required: [input.validations?.required || false],
      readOnly: [false]
    });
  }

  /**
   * Method to handle checkbox change event
   * @param element - FormGroup element representing the row in the table
   */
  checkboxChanged(element: FormGroup) {
    const isChecked = element.get('isSelected')?.value;
    const key = element.value.key;
    // If the checkbox is checked, set the value to an empty string and store the default value
    this.issueTmplArray.controls.forEach((el: any) => {
      if (el.value.key === key) {
        if (isChecked) {
          el.value.defaultValue = el.value.value;
          el.get('value').setValue('');
        } else {
          el.get('value').setValue(el.value.defaultValue);
        }
      }
    });

  }

  /**
   * Method to initialize the form group for issue template table
   */
  initializeForm() {
    this.issueTmplForm = this.fb.group({
      issueTmplArray: this.fb.array([])
    });
  }

  /** Method to add table data */
  addTableData(inputs: any[]) {
    this.initializeForm(); // Initialize the form to reset it

    // Push form controls to issueTmplArray FormArray
    inputs?.forEach((input: any) => {
      if (this.readOnlyFields[0].toUpperCase() === input.id.toUpperCase()) {
        input.attributes.value = this.authService?.gmId || ''; // Set the value of read-only field to GMID
      }

      const formGroup = this.createFormGroup(input);
      this.issueTmplArray.push(formGroup);
    })

    // If the mode is 'read' or 'edit', check if the value contains a dollar sign and set isSelected accordingly
    if (this.mode === 'read' || this.mode === 'edit') {
      this.issueTmplArray?.controls?.forEach((input: any) => {
        if (typeof input?.value?.value === "string" && input?.value?.value?.includes("$") && !(input?.value?.value?.includes("${{"))) {
          input?.get('isSelected').setValue(true);
        } else {
          input.get('isSelected').setValue(false);
        }
      })
    }
  }
}
